import type {
  CountryFieldModel,
  CountryListModel,
} from 'src/models/country.models'

/**
 * The address fields this fieldset renders, in the app's own vocabulary
 * (`address1` / `address2` / …) rather than any one API's.
 *
 * This is deliberate: the shared country catalogue keys its per-country field
 * rules by exactly these names (`CountryFieldModel['name']` is
 * `Exclude<keyof Address, 'country'>`), so speaking the catalogue's language
 * here means no translation layer inside the fieldset. Callers whose transport
 * uses different names — Card Management's `line1` / `line2` — map at their own
 * boundary, where the mapping is visible next to the request it builds.
 */
export type BillingAddressValues = {
  address1: string
  address2: string
  city: string
  province: string
  postal: string
  country: string
}

export type BillingAddressField = Exclude<keyof BillingAddressValues, 'country'>

export type BillingAddressErrors = Partial<
  Record<keyof BillingAddressValues, string>
>

export const BILLING_ADDRESS_FIELD_ORDER: BillingAddressField[] = [
  'address1',
  'address2',
  'city',
  'province',
  'postal',
]

/**
 * The catalogue's rules for one country, in render order.
 *
 * Returns `[]` for an unknown or not-yet-loaded country. Callers must treat an
 * empty list as "rules unknown", never as "no fields required" — validating
 * against `[]` would pass every value.
 */
export const billingAddressFieldsForCountry = (
  countriesByIso: Record<string, CountryListModel>,
  country: string,
): CountryFieldModel[] => {
  const fields = countriesByIso[country]?.fields
  if (fields === undefined) {
    return []
  }

  return BILLING_ADDRESS_FIELD_ORDER.map((name) =>
    fields.find((field) => field.name === name),
  ).filter((field): field is CountryFieldModel => field !== undefined)
}

/**
 * Validate values against the selected country's rules.
 *
 * Only the country's own required/format rules apply — there is no hardcoded
 * "state and ZIP are required" anywhere, because that is a US rule and this
 * form is not US-only. A country whose catalogue entry omits `province`
 * produces no province error, and the field is not rendered either.
 *
 * Returns `{}` when the rules have not loaded, so a pending catalogue cannot
 * manufacture errors on fields the user was never shown.
 */
export const validateBillingAddress = (
  values: BillingAddressValues,
  fields: CountryFieldModel[],
  messages: {
    required: (label: string) => string
    invalid: (label: string) => string
    /**
     * Localized "Country". Every other label comes from the catalogue, which
     * is already localized — hardcoding this one produced a mixed-language
     * error ("Country" in English inside a translated template).
     */
    countryLabel: string
  },
): BillingAddressErrors => {
  const errors: BillingAddressErrors = {}

  if (values.country === '') {
    errors.country = messages.required(messages.countryLabel)
    return errors
  }
  if (fields.length === 0) {
    return errors
  }

  fields.forEach((field) => {
    const value = values[field.name as BillingAddressField] ?? ''

    if (!field.optional && value.trim() === '') {
      errors[field.name as BillingAddressField] = messages.required(field.label)
      return
    }
    // Format is checked only on a non-empty value: an optional field left blank
    // is valid, and a required blank one already reported the clearer message.
    if (field.format !== undefined && value !== '') {
      if (!new RegExp(field.format).test(value)) {
        errors[field.name as BillingAddressField] = messages.invalid(
          field.label,
        )
      }
    }
  })

  return errors
}

/**
 * Drop values for fields the selected country does not use.
 *
 * Switching country must not silently carry a previous country's ZIP into the
 * request — on a complete-set PUT that value would be persisted, or rejected by
 * the server's country rules, with no field on screen explaining why.
 */
export const clearUnusedBillingAddressFields = (
  values: BillingAddressValues,
  fields: CountryFieldModel[],
): BillingAddressValues => {
  if (fields.length === 0) {
    return values
  }
  const used = new Set(fields.map((field) => field.name))

  return BILLING_ADDRESS_FIELD_ORDER.reduce<BillingAddressValues>(
    (next, name) => (used.has(name) ? next : { ...next, [name]: '' }),
    values,
  )
}
